// Rotary encoder drawn as inline SVG (Studio skin look: a rubbery cap inside an LED arc).
// Vertical drag (Shift = fine), wheel, double-click reset, arrow keys when focused. The math is
// ported from the desktop app (knobMath.ts).
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from "react";
import {
  arcPath,
  clamp,
  DRAG_PX_FULL,
  FINE_FACTOR,
  formatKnobValue,
  KNOB_END_DEG,
  KNOB_START_DEG,
  normToValue,
  nudgeValue,
  snapToStep,
  valueArcAngles,
  valueToAngle,
  valueToNorm,
} from "./knobMath";

export interface KnobProps {
  value: number;
  min: number;
  max: number;
  defaultValue?: number;
  label: string;
  size?: number;
  color?: string;
  format?: (v: number) => string;
  bipolar?: boolean;
  step?: number;
  log?: boolean;
  onChange: (v: number) => void;
  onChangeEnd?: () => void;
  disabled?: boolean;
  testId?: string;
}

const C = 50;
const R_TRACK = 44;
const R_BODY = 31;

export function Knob(props: KnobProps) {
  const {
    value,
    min,
    max,
    label,
    size = 54,
    color = "var(--accent)",
    format,
    bipolar = false,
    log = false,
    disabled = false,
  } = props;
  const id = useId();
  const [dragging, setDragging] = useState(false);
  const propsRef = useRef(props);
  propsRef.current = props;
  const rootRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ pointerId: number; lastY: number; norm: number } | null>(null);
  const wheelTimer = useRef<number | undefined>(undefined);

  const resetValue = useCallback((): number => {
    const p = propsRef.current;
    if (p.defaultValue !== undefined) return p.defaultValue;
    if (p.bipolar) return p.min < 0 && p.max > 0 ? 0 : (p.min + p.max) / 2;
    return p.min;
  }, []);

  const emit = useCallback((v: number) => {
    const p = propsRef.current;
    if (v !== p.value) p.onChange(v);
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const p = propsRef.current;
      if (p.disabled) return;
      e.preventDefault();
      const raw = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? -e.deltaY : e.deltaX;
      if (raw === 0) return;
      const ticks = e.deltaMode === 0 ? clamp(raw / 25, -4, 4) : Math.sign(raw);
      emit(nudgeValue(p.value, ticks, { min: p.min, max: p.max, log: p.log, step: p.step, fine: e.shiftKey }));
      window.clearTimeout(wheelTimer.current);
      wheelTimer.current = window.setTimeout(() => propsRef.current.onChangeEnd?.(), 250);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      window.clearTimeout(wheelTimer.current);
    };
  }, [emit]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || e.button !== 0) return;
    e.preventDefault();
    rootRef.current?.focus({ preventScroll: true });
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { pointerId: e.pointerId, lastY: e.clientY, norm: valueToNorm(value, min, max, log) };
    setDragging(true);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dy = e.clientY - d.lastY;
    d.lastY = e.clientY;
    if (dy === 0) return;
    const p = propsRef.current;
    d.norm = clamp(d.norm - dy / (DRAG_PX_FULL * (e.shiftKey ? FINE_FACTOR : 1)), 0, 1);
    emit(snapToStep(normToValue(d.norm, p.min, p.max, p.log), p.step, p.min, p.max));
  };
  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    propsRef.current.onChangeEnd?.();
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const p = propsRef.current;
    const o = { min: p.min, max: p.max, log: p.log, step: p.step, fine: e.shiftKey };
    const next: Record<string, () => number> = {
      ArrowUp: () => nudgeValue(p.value, 1, o),
      ArrowRight: () => nudgeValue(p.value, 1, o),
      ArrowDown: () => nudgeValue(p.value, -1, o),
      ArrowLeft: () => nudgeValue(p.value, -1, o),
      PageUp: () => nudgeValue(p.value, 10, o),
      PageDown: () => nudgeValue(p.value, -10, o),
      Home: () => p.min,
      End: () => p.max,
      Backspace: resetValue,
      Delete: resetValue,
    };
    const f = next[e.key];
    if (!f) return;
    e.preventDefault();
    e.stopPropagation();
    emit(f());
  };
  const onKeyUp = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (
      [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "PageUp",
        "PageDown",
        "Home",
        "End",
        "Backspace",
        "Delete",
      ].includes(e.key)
    ) {
      propsRef.current.onChangeEnd?.();
    }
  };

  const text = format ? format(value) : formatKnobValue(value, min, max);
  const angle = valueToAngle(value, min, max, log);
  const [a0, a1] = valueArcAngles(value, min, max, bipolar, log);
  const trackPath = arcPath(C, C, R_TRACK, KNOB_START_DEG, KNOB_END_DEG);
  const valuePath = arcPath(C, C, R_TRACK, a0, a1);

  return (
    <div
      ref={rootRef}
      className="knob"
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-labelledby={`${id}-label`}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={Number(value.toFixed(4))}
      aria-valuetext={text}
      aria-disabled={disabled || undefined}
      data-dragging={dragging || undefined}
      data-testid={props.testId}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={() => {
        if (disabled) return;
        emit(resetValue());
        propsRef.current.onChangeEnd?.();
      }}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
    >
      <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
        <path d={trackPath} stroke="rgba(0,0,0,0.55)" strokeWidth={8} strokeLinecap="round" fill="none" />
        {valuePath ? (
          <path d={valuePath} stroke={color} strokeWidth={6} strokeLinecap="round" fill="none" className="knob-led" />
        ) : null}
        <circle cx={C} cy={C + 3} r={R_BODY} fill="rgba(0,0,0,0.45)" />
        <circle cx={C} cy={C} r={R_BODY} fill="url(#knob-cap)" stroke="rgba(255,255,255,0.12)" strokeWidth={1.5} />
        <circle cx={C} cy={C} r={R_BODY - 7} fill="none" stroke="rgba(0,0,0,0.25)" strokeWidth={2} />
        <g transform={`rotate(${angle.toFixed(2)} ${C} ${C})`}>
          <line
            x1={C}
            y1={C - R_BODY + 4}
            x2={C}
            y2={C - R_BODY + 14}
            stroke="#f4f6fb"
            strokeWidth={5}
            strokeLinecap="round"
          />
        </g>
        <defs>
          <radialGradient id="knob-cap" cx="40%" cy="32%" r="75%">
            <stop offset="0" stopColor="#5a6479" />
            <stop offset="0.6" stopColor="#3a4255" />
            <stop offset="1" stopColor="#262c3a" />
          </radialGradient>
        </defs>
      </svg>
      <span id={`${id}-label`} className="knob-label">
        {label}
      </span>
      <span className="knob-value">{text}</span>
    </div>
  );
}
